/**
 * Sleep Plan Notifications
 *
 * Handles scheduling and sending local notifications for wake window alerts.
 * Uses the Capacitor Local Notifications plugin on native,
 * falls back to web Notification API on web.
 */

import type { WakeWindowRange } from './wakeWindowCalculator';

export interface SleepNotificationSchedule {
  earlyWarningAt: Date | null;
  windowOpensAt: Date | null;
  windowClosesAt: Date | null;
  overtiredAt: Date | null;
}

/**
 * Calculate notification schedule based on last wake time
 */
export function calculateNotificationSchedule(
  lastWakeTime: Date,
  windowRange: WakeWindowRange,
  quietStart: string = '22:00',
  quietEnd: string = '06:00',
  enabledTypes: {
    earlyWarning: boolean;
    windowOpens: boolean;
    windowCloses: boolean;
    overtired: boolean;
  } = { earlyWarning: true, windowOpens: true, windowCloses: true, overtired: true },
): SleepNotificationSchedule {
  const { minMinutes, maxMinutes } = windowRange;

  const earlyWarningMinutes = Math.floor(minMinutes * 0.7);
  const overtiredMinutes = Math.floor(maxMinutes * 1.2);

  const addMinutes = (base: Date, mins: number): Date => {
    return new Date(base.getTime() + mins * 60 * 1000);
  };

  const isInQuietHours = (date: Date): boolean => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

    if (quietStart <= quietEnd) {
      return timeStr >= quietStart && timeStr < quietEnd;
    }
    // Overnight quiet hours (e.g., 22:00 - 06:00)
    return timeStr >= quietStart || timeStr < quietEnd;
  };

  const getNotificationTime = (mins: number): Date | null => {
    const time = addMinutes(lastWakeTime, mins);
    if (time <= new Date()) return null; // Already past
    if (isInQuietHours(time)) return null; // In quiet hours
    return time;
  };

  return {
    earlyWarningAt: enabledTypes.earlyWarning ? getNotificationTime(earlyWarningMinutes) : null,
    windowOpensAt: enabledTypes.windowOpens ? getNotificationTime(minMinutes) : null,
    windowClosesAt: enabledTypes.windowCloses ? getNotificationTime(maxMinutes) : null,
    overtiredAt: enabledTypes.overtired ? getNotificationTime(overtiredMinutes) : null,
  };
}

/**
 * Schedule local notifications for the wake window.
 * On native, uses Capacitor Local Notifications.
 * On web, uses setTimeout + Notification API.
 */
export async function scheduleWakeWindowNotifications(
  schedule: SleepNotificationSchedule,
  _babyName: string,
  translations: {
    earlyWarning: { title: string; body: string };
    windowOpen: { title: string; body: string };
    windowClosing: { title: string; body: string };
    overtired: { title: string; body: string };
  },
): Promise<void> {
  // Cancel any existing scheduled notifications first
  await cancelSleepPlanNotifications();

  // Server-side notifications are handled by the cron job.
  // Client-side: use web Notification API as a fallback for immediate feedback.
  if ('Notification' in window && Notification.permission === 'granted') {
    const scheduleWebNotification = (at: Date | null, title: string, body: string) => {
      if (!at) return;
      const delay = at.getTime() - Date.now();
      if (delay > 0) {
        setTimeout(() => {
          new Notification(title, { body, icon: '/pp_logo-piktogram-w100.svg' });
        }, delay);
      }
    };

    scheduleWebNotification(schedule.earlyWarningAt, translations.earlyWarning.title, translations.earlyWarning.body);
    scheduleWebNotification(schedule.windowOpensAt, translations.windowOpen.title, translations.windowOpen.body);
    scheduleWebNotification(schedule.windowClosesAt, translations.windowClosing.title, translations.windowClosing.body);
    scheduleWebNotification(schedule.overtiredAt, translations.overtired.title, translations.overtired.body);
  }
}

/**
 * Cancel all sleep plan notifications
 */
export async function cancelSleepPlanNotifications(): Promise<void> {
  // Server-side notifications are managed by the cron job clearing next_notification_at.
  // Client-side web timeouts are not tracked, they'll fire or expire naturally.
}
